import React, { useEffect, useState } from "react";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  defaultCheck,
  discord,
  facebook,
  greenCheck,
  hidepassword,
  meta,
  showpassword,
  xIcon,
} from "@/constants";
import { useRegisterUser } from "@/services/auth.service";
import EmailModal from "./MetaEmailDialog";
import { Input } from "@/components/ui/input";
import { authApiURL } from "@/services/api.service";
import StatusDialog from "@/components/dialogs/StatusDialog";
import TermsOfServiceDialog from "./TermsOfServiceDialog";
import LoadingSpinner from "@/components/loaders/LoadingSpinner";
import { useLocation } from "react-router-dom";

interface PasswordValidationResults {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  specialChar: boolean;
}

const passwordCriteria = {
  length: "8 min character",
  uppercase: "1 UPPER CASE letter",
  lowercase: "1 lower case letter",
  specialChar: "1 special character",
};

const passwordSchema = z
  .string()
  .min(8, "Weak Password")
  .regex(/[A-Z]/, "1 UPPER CASE letter")
  .regex(/[a-z]/, "1 lower case letter")
  .regex(/[\W_]/, "1 special character");

// Define the validation schema using Zod
const signupSchema = z.object({
  username: z.string().min(1, "Required"),
  email: z
    .string()
    .min(1, "Required")
    .email("Email is invalid. Try another one."),
  password: passwordSchema,
  confirmPassword: z.string().min(1, "Required"),
});
// .refine((data) => data.password === data.confirmPassword, {
//   message: "Password Not Matched. Please check again.",
//   path: ["confirmPassword"], // This is where the error will be attached in the error object
// });

// Define the form inputs' type
type SignUpFormData = z.infer<typeof signupSchema>;

interface SignUpProps {
  onClose: () => void;
}

const SignupForm: React.FC<SignUpProps> = ({ onClose }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const refCode = queryParams.get("refCode");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signupSchema),
  });
  const [passwordStrengthLevel, setPasswordStrengthLevel] = useState(0);
  const [passwordValidationResults, setPasswordValidationResults] =
    useState<PasswordValidationResults>({
      length: false,
      uppercase: false,
      lowercase: false,
      specialChar: false,
    });
  const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false);

  const { mutateAsync: registerUser } = useRegisterUser();

  const [loginError, setLoginError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [usernameError, setUsernameError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [checkError, setCheckError] = useState<string>("");

  const password = watch("password", "");

  useEffect(() => {
    const validatePassword = (password: string): PasswordValidationResults => {
      const results = passwordSchema.safeParse(password);
      const passwordErrors: PasswordValidationResults = {
        uppercase: false,
        length: false,
        lowercase: false,
        specialChar: false,
      };

      if (!results.success) {
        for (const error of results.error.errors) {
          const message = error.message;

          if (message === passwordCriteria.length) {
            passwordErrors.length = true;
          } else if (message === passwordCriteria.uppercase) {
            passwordErrors.uppercase = true;
          } else if (message === passwordCriteria.lowercase) {
            passwordErrors.lowercase = true;
          } else if (message === passwordCriteria.specialChar) {
            passwordErrors.specialChar = true;
          }
        }
      }

      return passwordErrors;
    };
    setPasswordValidationResults(validatePassword(password));

    const getPasswordStrength = (password: string) => {
      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasSpecialChar = /[\W_]/.test(password);

      let strength = 0;
      if (
        password.length > 10 &&
        hasLowercase &&
        hasSpecialChar &&
        hasUppercase
      )
        strength = 4;
      else if (
        password.length > 7 &&
        hasLowercase &&
        hasSpecialChar &&
        hasUppercase
      )
        strength = 3;
      else if (
        password.length > 5 &&
        ((hasLowercase && hasSpecialChar) ||
          (hasLowercase && hasUppercase) ||
          (hasSpecialChar && hasUppercase))
      )
        strength = 2;
      else if (
        password.length > 1 &&
        (hasLowercase || hasSpecialChar || hasUppercase)
      )
        strength = 1;

      return strength;
    };
    const strength = getPasswordStrength(password);
    setPasswordStrengthLevel(strength);
  }, [password]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const onSubmit: SubmitHandler<SignUpFormData> = async (data) => {
    try {
      if (!isChecked) {
        setCheckError("You must agree to the terms and conditions.");
        return; // Stop the form submission
      }

      const { username, email, password } = data;
      await registerUser({
        refCode: refCode ?? "",
        userName: username,
        email,
        password,
        confirmPassword: data.confirmPassword,
      });

      setShowVerifyModal(true);
      // Optionally redirect the user or clear the form
    } catch (error: any) {
      setCheckError("");
      let messsage: string = "Error login";
      if (error && error.response && error.response?.data?.error) {
        messsage = error.response.data.error;
        // toast.error(messsage);
        setLoginError(messsage);
        console.log(loginError);
      }

      if (error && error.response && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (errors.email) {
          setEmailError(errors.email);
        }
        if (errors.userName) {
          setUsernameError("This username is taken. Try another username.");
          // onClose();
        }
        if (errors.passwordMatch) {
          setConfirmPasswordError("Password Not Matched. Please check again.");
        }
      }
    }
  };

  // Function to determine the strength bar color
  const getStrengthBarColor = (level: number) => {
    switch (level) {
      case 1:
        return "bg-[#FF183D]";
      case 2:
        return "bg-[#FFF618]";
      case 3:
        return "bg-[#8FFF00]";
      case 4:
        return "bg-[#24FF00]";
      default:
        return "bg-gray-500";
    }
  };

  const getStrengthTextColor = (level: number) => {
    switch (level) {
      case 1:
        return "text-[#FF183D]";
      case 2:
        return "text-[#FFF618]";
      case 3:
        return "text-[#8FFF00]";
      case 4:
        return "text-[#24FF00]";
      default:
        return "text-gray-500";
    }
  };

  const handleSocialLogin = (provider: string) => {
    const authUrl = `${authApiURL}/${provider}/auth`;
    window.location.href = authUrl;
  };

  // to reset email, username and confirm password error messages when the input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // Clear corresponding error message when input changes
    if (name === "email") setEmailError("");
    else if (name === "username") setUsernameError("");
    else if (name === "confirmPassword") setConfirmPasswordError("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)();
    }
  };

  return (
    <>
      <div className="space-y-2 sm:space-y-3" onKeyDown={handleKeyDown}>
        {/* Username field */}
        <div className="text-base font-semibold sm:hidden">
          Sign Up to Sharkroll
        </div>
        <div>
          <div className="relative">
            <Input
              id="username"
              {...register("username")}
              type="text"
              placeholder="Username"
              onChange={handleInputChange} // Add onChange event handler
              startAdornment={<img src="/assets/profile.png" alt="profile" />}
              className={`mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm ${errors.username && "bg-[#FB4245]/30"}`}
            />
            {usernameError && (
              <p className="mt-1.5 text-xs font-[500] text-danger">
                {usernameError}
              </p>
            )}
          </div>
        </div>

        {/* Email field */}
        <div>
          <div className="relative">
            <Input
              id="email"
              {...register("email")}
              type="text"
              placeholder="Email"
              startAdornment={<img src="/assets/email.png" alt="email" />}
              onChange={handleInputChange} // Add onChange event handler
              className={`mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm ${errors.username && "bg-[#FB4245]/30"}`}
            />
            {emailError && (
              <p className="mt-1.5 text-xs font-[500] text-danger">{emailError}</p>
            )}
          </div>
        </div>

        {/* Password field */}
        <div>
          <Input
            id="password"
            {...register("password")}
            type={showCurrentPassword ? "text" : "password"}
            startAdornment={<img src="/assets/password.png" alt="password" />}
            endAdornment={
              <img
                src={showCurrentPassword ? showpassword : hidepassword}
                className="mr-3 size-6 cursor-pointer"
                onClick={() =>
                  setShowCurrentPassword((prevState) => !prevState)
                }
              />
            }
            placeholder="Password"
            className={`mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm ${errors.username && "bg-[#FB4245]/30"}`}
          />

          {/* Password strength bar */}
          <div className="mt-2 flex">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className={`ml-1 h-1 flex-1 rounded ${
                  index < passwordStrengthLevel
                    ? getStrengthBarColor(passwordStrengthLevel)
                    : "bg-white/5"
                }`}
              ></div>
            ))}
          </div>

          {/* Password Validation Results */}
          <div className="mt-2 flex w-full justify-between gap-4">
            <div className="grid grid-rows-2 gap-1">
              <div className="grid grid-cols-2">
                <div
                  className={"flex w-36 items-center gap-2 text-xs text-white"}
                >
                  {!passwordValidationResults.uppercase ? (
                    <img src={greenCheck} alt="checkuppercase" />
                  ) : (
                    <img src={defaultCheck} alt="checkuppercasedefault" />
                  )}{" "}
                  {passwordCriteria.uppercase}
                </div>
                <div
                  className={"flex w-36 items-center gap-2 text-xs text-white"}
                >
                  {!passwordValidationResults.specialChar ? (
                    <img src={greenCheck} alt="checkspecialChar" />
                  ) : (
                    <img src={defaultCheck} alt="checkspecialChardefault" />
                  )}{" "}
                  {passwordCriteria.specialChar}
                </div>
              </div>
              <div className="grid grid-cols-2">
                <div
                  className={"flex w-36 items-center gap-2 text-xs text-white"}
                >
                  {!passwordValidationResults.lowercase ? (
                    <img src={greenCheck} alt="checklowercase" />
                  ) : (
                    <img src={defaultCheck} alt="checklowercasedefault" />
                  )}{" "}
                  {passwordCriteria.lowercase}
                </div>

                <div
                  className={"flex w-36 items-center gap-2 text-xs text-white"}
                >
                  {password.length >= 8 ? (
                    <img src={greenCheck} alt="checklength" />
                  ) : (
                    <img src={defaultCheck} alt="checklengthdefault" />
                  )}{" "}
                  {passwordCriteria.length}
                </div>
              </div>
            </div>
            <p
              className={`mt-1 text-end text-sm ${getStrengthTextColor(passwordStrengthLevel)}`}
            >
              {" "}
              {
                [
                  " ",
                  "Weak Password",
                  "Moderate Password",
                  "Good Password",
                  "Strong Password",
                ][passwordStrengthLevel]
              }
            </p>
          </div>
        </div>

        {/* Confirm Password field */}
        <div className="relative">
          <Input
            id="confirmPassword"
            {...register("confirmPassword")}
            type={showConfirmPassword ? "text" : "password"}
            onChange={handleInputChange} // Add onChange event handler
            startAdornment={<img src="/assets/password.png" alt="password" />}
            endAdornment={
              <img
                src={showConfirmPassword ? showpassword : hidepassword}
                className="mr-3 size-6 cursor-pointer"
                onClick={() =>
                  setShowConfirmPassword((prevState) => !prevState)
                }
              />
            }
            placeholder="Confirm Password"
            className={`mt-1 w-full rounded-md border-2 border-solid border-white/5 bg-white/5 px-3 py-2 pl-9 text-white placeholder:text-white sm:text-sm ${errors.username && "bg-[#FB4245]/30"}`}
          />
         {confirmPasswordError && (
          <p className="mt-1.5 text-xs font-[500] text-danger">{confirmPasswordError}</p>
        )}
        </div>

        {/* Terms of Service Checkbox */}
        <div className="flex">
          <input
            type="checkbox"
            color="success"
            className="mt-1 h-4 w-4 bg-white/35"
            checked={isChecked}
            onChange={handleCheckboxChange as any}
          />
          <label
            htmlFor="termsOfService"
            className="ml-2 text-[11px] font-normal text-white/50"
          >
            I hereby confirm that I am at least 18 years old or have reached the
            legal age of majority in my jurisdiction. I acknowledge that I have
            read and agree to the{" "}
            <TermsOfServiceDialog
              trigger={
                <span className="cursor-pointer text-white underline">
                  Terms of Service.
                </span>
              }
            />
          </label>
        </div>
        <svg
          width="375"
          height="1"
          viewBox="0 0 424 1"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line opacity="0.2" y1="0.5" x2="424" y2="0.5" stroke="#69EAFF" />
        </svg>

        <div className="flex items-center justify-center gap-4">
          <Button
            type="button"
            aria-label="Login with x"
            className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
            onClick={() => handleSocialLogin("twitter")}
          >
            <img
              className="opacity-50 group-hover:opacity-100"
              src={xIcon}
              alt="xIcon"
            />
          </Button>
          <Button
            type="button"
            aria-label="Login with facebook"
            className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
            onClick={() => handleSocialLogin("facebook")}
          >
            <img
              className="opacity-50 group-hover:opacity-100"
              src={facebook}
              alt="facebook"
            />
          </Button>
          <Button
            type="button"
            aria-label="Login with discord"
            className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
            onClick={() => handleSocialLogin("discord")}
          >
            <img
              className="opacity-50 group-hover:opacity-100"
              src={discord}
              alt="discord"
            />
          </Button>
          <EmailModal
            trigger={
              <Button
                type="button"
                aria-label="Login with meta"
                className="group flex w-full items-center justify-center rounded-md bg-white/0 px-4 py-2 text-white hover:bg-white/10"
              >
                <img
                  className="opacity-50 group-hover:opacity-100"
                  src={meta}
                  alt="meta"
                />
              </Button>
            }
          />
        </div>

        {/* Submit button */}
        <Button
          onClick={handleSubmit(onSubmit)}
          className="mt-3 w-full rounded-md bg-button-gradient hover:bg-button-gradient-inverted"
        >
          {"Create Account"}
        </Button>
        <StatusDialog
          status="success"
          title="Successful!"
          onClose={onClose}
          message={
            <>
              Registration link successfully sent to your email!
              <br />
              Please check the email!.
            </>
          }
          buttonText="Close"
          isOpen={showVerifyModal}
          setIsOpen={() => setShowVerifyModal(false)}
        />
      </div>
      {checkError && (
        <div className="mt-2 text-xs text-red-500">{checkError}</div>
      )}
    </>
  );
};

export default SignupForm;
